import React, {
    useState,
    useEffect
} from "react";

import useConfirmation
    from "../../hooks/useConfirmation";

import ConfirmationModal
    from "../components/ConfirmationModal";

import db from "../../constants/db";

export default function MultiOccupancyPopup({
    popupDesk,
    activities,
    setWorkplaces,
    setPopupDesk,
    onConfirm,
    onDeselect
}) {

    const [selectedId, setSelectedId] =
        useState(null);

    const [capacity, setCapacity] =
        useState(0);

    const [isPortrait, setIsPortrait] =
        useState(
            window.innerHeight >
            window.innerWidth
        );

    const {
        confirm,
        modalProps,
        handleModalConfirm,
        handleModalCancel
    } = useConfirmation();

    /**************************************************************************
     * Orientation Handler
     *************************************************************************/
    useEffect(function () {

        function handleResize() {

            setIsPortrait(
                window.innerHeight >
                window.innerWidth
            );
        }

        window.addEventListener(
            "resize",
            handleResize
        );

        return function () {

            window.removeEventListener(
                "resize",
                handleResize
            );
        };

    }, []);

    /**************************************************************************
     * Initialise Popup State
     *************************************************************************/
    useEffect(function () {

        if (popupDesk) {

            setSelectedId(
                popupDesk.selectedActivityId || null
            );

            setCapacity(
                popupDesk.capacity || 0
            );
        }

    }, [popupDesk]);

    /**************************************************************************
     * Hide Popup
     *************************************************************************/
    if (!popupDesk) {

        return null;
    }

    /**************************************************************************
     * Capacity Update
     *************************************************************************/
    function updateCapacity(newCap) {

        setCapacity(newCap);

        setWorkplaces(function (prev) {

            return prev.map(function (d) {

                if (d.id === popupDesk.id) {

                    return {
                        ...d,
                        capacity: newCap
                    };
                }

                return d;
            });
        });
    }

    /**************************************************************************
     * Confirm
     *************************************************************************/
    async function handleConfirm() {

        if (!selectedId) {

            await confirm({

                title: "Activity Required",

                message:
                    "Please select an activity.",

                confirmText: "OK",

                hideCancel: true,

                confirmColor: "#ef4444"
            });

            return;
        }

        const selectedActivity =
            activities.find(function (a) {

                return a.id === selectedId;
            });

        const activityName =
            selectedActivity &&
            selectedActivity.name
                ? selectedActivity.name.toLowerCase()
                : "";

        const ignoreCapacity =
            activityName === "vacant" ||
            activityName === "unavailable";

        if (
            ignoreCapacity &&
            capacity > 0
        ) {

            await confirm({

                title: "Invalid Occupancy",

                message:
                    "Vacant or Unavailable spaces must have zero occupants.",

                confirmText: "OK",

                hideCancel: true,

                confirmColor: "#ef4444"
            });

            return;
        }

        if (!ignoreCapacity && capacity <= 0) {

            await confirm({

                title: "Occupancy Required",

                message:
                    "Please enter occupant count.",

                confirmText: "OK",

                hideCancel: true,

                confirmColor: "#ef4444"
            });

            return;
        }

        if (
            popupDesk.capacity &&
            capacity > popupDesk.capacity
        ) {

            const proceed =
                await confirm({

                    title: "Capacity Warning",

                    message:
                        "Occupancy exceeds room capacity (" +
                        popupDesk.capacity +
                        "). Continue anyway?",

                    confirmText: "Continue",

                    cancelText: "Cancel",

                    confirmColor: "#f59e0b"
                });

            if (!proceed) {

                return;
            }
        }

        if (onConfirm) {

            onConfirm(
                popupDesk,
                selectedId,
                1,
                capacity
            );
        }
    }

    /**************************************************************************
     * Number Button Style
     *************************************************************************/
    function getKeyStyle() {

        return {

            background:
                "linear-gradient(180deg,#ffffff 0%, #e9eef5 100%)",

            color: "#0f172a",

            border:
                "1px solid rgba(15,23,42,0.08)",

            borderRadius: "12px",

            padding: "10px",

            fontSize: "13px",

            fontWeight: "700",

            cursor: "pointer",

            boxShadow:
                "0 3px 8px rgba(15,23,42,0.08)",

            transition: "all 0.15s ease"
        };
    }

    /**************************************************************************
     * Render
     *************************************************************************/
    return (

        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.42)",
                backdropFilter: "blur(6px)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "14px"
            }}
        >

            <div
                style={{
                    width: isPortrait
                        ? "680px"
                        : "900px",

                    maxWidth: "95vw",

                    maxHeight: "92vh",

                    borderRadius: "18px",

                    overflow: "hidden",

                    background:
                        "rgba(255,255,255,0.92)",

                    backdropFilter:
                        "blur(18px)",

                    border:
                        "1px solid rgba(255,255,255,0.55)",

                    boxShadow:
                        "0 30px 80px rgba(0,0,0,0.35)",

                    display: "flex",

                    flexDirection: "column"
                }}
            >

                {/* HEADER */}
                <div
                    style={{
                        padding: "8px 14px",

                        borderBottom:
                            "1px solid rgba(15,23,42,0.08)",

                        background:
                            "linear-gradient(180deg,#f8fafc 0%, #eef2f7 100%)",

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "space-between",

                        gap: "16px"
                    }}
                >

                    <div
                        style={{
                            minWidth: 0,
                            flex: 1
                        }}
                    >

                        <div
                            style={{
                                fontSize: "17px",
                                fontWeight: "700",
                                color: "#0f172a",
                                lineHeight: 1.1
                            }}
                        >
                            {popupDesk.code}
                        </div>

                        <div
                            style={{
                                marginTop: "2px",
                                fontSize: "10px",
                                color: "#64748b",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            {popupDesk.roomDescription}
                        </div>

                    </div>

                    <div
                        style={{
                            flexShrink: 0,
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#5a9bd6"
                        }}
                    >
                        Capacity: {popupDesk.capacity || "Unknown"}
                    </div>

                </div>

                {/* MAIN */}
                <div
                    style={{
                        flex: 1,

                        display: "flex",

                        flexDirection: isPortrait
                            ? "column"
                            : "row",

                        gap: "10px",

                        padding: "10px",

                        overflow: "hidden",

                        minHeight: 0
                    }}
                >

                    {/* ACTIVITIES */}
                    <div
                        style={{
                            flex: isPortrait
                                ? "0 0 auto"
                                : "0 0 48%",

                            display: "flex",

                            flexDirection: "column",

                            minHeight: 0,

                            overflow: "hidden"
                        }}
                    >

                        <div
                            style={{
                                color: "#0f172a",
                                fontSize: "12px",
                                fontWeight: "700",
                                marginBottom: "8px"
                            }}
                        >
                            Activities
                        </div>

                        <div
                            style={{
                                display: "flex",

                                flexDirection: "column",

                                gap: "8px",

                                overflowY: "auto",

                                minHeight: 0,

                                paddingRight: "4px"
                            }}
                        >

                            {activities.map(function (act) {

                                const isSelected =
                                    selectedId === act.id;

                                return (

                                    <button
                                        key={act.id}

                                        onClick={function () {

                                            setSelectedId(act.id);

                                            setWorkplaces(function (prev) {

                                                return prev.map(function (d) {

                                                    if (d.id === popupDesk.id) {

                                                        return {
                                                            ...d,
                                                            selectedActivityId: act.id
                                                        };
                                                    }

                                                    return d;
                                                });
                                            });
                                        }}

                                        style={{

                                            background: act.color,

                                            color: "#ffffff",

                                            border: isSelected
                                                ? "3px solid #0f172a"
                                                : "3px solid transparent",

                                            borderRadius: "12px",

                                            padding: "10px 12px",

                                            textAlign: "left",

                                            fontSize: "14px",

                                            fontWeight: isSelected
                                                ? "700"
                                                : "500",

                                            cursor: "pointer",

                                            boxShadow: isSelected
                                                ? "0 6px 14px rgba(15,23,42,0.18)"
                                                : "0 3px 8px rgba(15,23,42,0.08)",

                                            transition:
                                                "all 0.15s ease"
                                        }}
                                    >

                                        {isSelected
                                            ? "✓ "
                                            : ""}

                                        {act.name}

                                    </button>
                                );
                            })}

                        </div>

                    </div>

                    {/* OCCUPANCY PANEL */}
                    <div
                        style={{
                            flex: 1,

                            borderRadius: "16px",

                            padding: "10px",

                            background:
                                "linear-gradient(180deg,#ffffff 0%, #f4f7fb 100%)",

                            border:
                                "1px solid rgba(15,23,42,0.08)",

                            boxShadow:
                                "0 4px 12px rgba(15,23,42,0.08)",

                            display: "flex",

                            flexDirection: "column"
                        }}
                    >

                        <div
                            style={{
                                color: "#0f172a",
                                fontSize: "12px",
                                fontWeight: "700",
                                marginBottom: "6px"
                            }}
                        >
                            Number of Occupants
                        </div>

                        {/* DISPLAY */}
                        <div
                            style={{
                                background:
                                    "linear-gradient(180deg,#ffffff 0%, #eef3f9 100%)",

                                borderRadius: "14px",

                                minHeight: "42px",

                                display: "flex",

                                alignItems: "center",

                                justifyContent: "center",

                                marginBottom: "8px",

                                border:
                                    "1px solid rgba(15,23,42,0.08)"
                            }}
                        >

                            <div
                                style={{
                                    color: "#0f172a",

                                    fontSize: "18px",

                                    fontWeight: "700"
                                }}
                            >
                                {capacity}
                            </div>

                        </div>

                        {/* KEYPAD */}
                        <div
                            style={{
                                display: "grid",

                                gridTemplateColumns:
                                    "repeat(3, 1fr)",

                                gap: "6px",

                                marginBottom: "10px"
                            }}
                        >

                            {[1,2,3,4,5,6,7,8,9,0].map(function (num) {

                                return (

                                    <button
                                        key={num}

                                        onClick={function () {

                                            updateCapacity(
                                                parseInt(
                                                    "" +
                                                    (capacity || "") +
                                                    num,
                                                    10
                                                )
                                            );
                                        }}

                                        style={getKeyStyle()}
                                    >
                                        {num}
                                    </button>
                                );
                            })}

                            <button
                                onClick={function () {

                                    updateCapacity(0);
                                }}

                                style={{
                                    gridColumn: "span 2",

                                    background:
                                        "linear-gradient(180deg,#ff4343 0%, #ff1f1f 100%)",

                                    color: "#ffffff",

                                    border: "none",

                                    borderRadius: "12px",

                                    padding: "10px",

                                    fontSize: "13px",

                                    fontWeight: "700",

                                    cursor: "pointer",

                                    boxShadow:
                                        "0 6px 14px rgba(255,0,0,0.16)"
                                }}
                            >
                                Clear
                            </button>

                        </div>

                        {/* QUICK BUTTONS */}
                        <div
                            style={{
                                display: "grid",

                                gridTemplateColumns:
                                    "repeat(3, 1fr)",

                                gap: "6px"
                            }}
                        >

                            {[
                                {
                                    label: "50-100",
                                    value: 75
                                },
                                {
                                    label: "100-200",
                                    value: 150
                                },
                                {
                                    label: "200+",
                                    value: 200
                                }
                            ].map(function (btn) {

                                return (

                                    <button
                                        key={btn.label}

                                        onClick={function () {

                                            updateCapacity(btn.value);
                                        }}

                                        style={getKeyStyle()}
                                    >
                                        {btn.label}
                                    </button>
                                );
                            })}

                        </div>

                    </div>

                </div>

                {/* FOOTER */}
                <div
                    style={{
                        display: "flex",

                        justifyContent: "space-between",

                        alignItems: "center",

                        padding: "6px 10px",

                        borderTop:
                            "1px solid rgba(15,23,42,0.08)",

                        background:
                            "linear-gradient(180deg,#f8fafc 0%, #eef2f7 100%)",

                        flexShrink: 0
                    }}
                >

                    <button
                        onClick={function () {

                            setPopupDesk(null);
                        }}

                        style={{
                            background:
                                "linear-gradient(180deg,#6b7280 0%, #4b5563 100%)",

                            color: "#ffffff",

                            border: "none",

                            borderRadius: "12px",

                            padding: "12px 22px",

                            fontSize: "12px",

                            fontWeight: "700",

                            cursor: "pointer",

                            boxShadow:
                                "0 6px 14px rgba(15,23,42,0.16)"
                        }}
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleConfirm}

                        style={{

                            background:
                                "linear-gradient(180deg, #6baee6 0%, #5a9bd6 45%, #4a8ac4 100%)",

                            color: "#ffffff",

                            border: "none",

                            borderRadius: "12px",

                            padding: "12px 22px",

                            fontSize: "12px",

                            fontWeight: "700",

                            cursor: "pointer",

                            boxShadow:
                                "0 6px 14px rgba(90,155,214,0.24)",

                            transition: "all 0.2s ease"
                        }}
                    >
                        Confirm Occupancy
                    </button>

                </div>

            </div>

            <ConfirmationModal
                {...modalProps}
                onConfirm={handleModalConfirm}
                onCancel={handleModalCancel}
            />

        </div>
    );
}